import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { CircularProgress } from '../circular-progress/circular-progress.js';
import { colors, colorScale } from '../../tokens/colors/colors.js';
import { fontWeight } from '../../tokens/fontWeight.js';
import { spacing } from '../../tokens/spacing/spacing.js';

function _Button({ variant = "primary", color = "default", size = "medium", disabled = false, loading = false, fullWidth = false, style, className, onClick, type = "button", children, shape = "default", startIcon, endIcon, ...props }, ref) {
    const _disabled = disabled || loading;
    return (jsx(StyledButton, { ref: ref, disabled: _disabled, onClick: onClick, "$fullWidth": fullWidth, "$variant": variant, "$size": size, "$color": color, "$shape": shape, style: style, className: className, type: type, ...props, children: jsxs(Box, { children: [loading && (jsx(ButtonCircularProgress, { size: "1rem", "$variant": variant, "$color": color, "$disabled": _disabled })), startIcon, children, endIcon] }) }));
}
const Button = forwardRef(_Button);
function getPadding({ $variant, $size, $shape, }) {
    if ($variant === "minimal") {
        return "0";
    }
    switch ($size) {
        case "x-small":
            return `${spacing.XS2} ${$shape === "default" ? spacing.S : spacing.XS}`;
        case "small":
            return `${spacing.XS} ${$shape === "default" ? spacing.M : spacing.XS}`;
        case "medium":
        default:
            return `${spacing.S} ${$shape === "default" ? spacing.L : spacing.S}`;
    }
}
function getFontSize({ $size }) {
    switch ($size) {
        case "x-small":
        case "small":
            return "0.875rem";
        case "medium":
        default: {
            return "1rem";
        }
    }
}
function getTextdecoration({ $variant }) {
    switch ($variant) {
        case "minimal":
            return "underline";
        default: {
            return "unset";
        }
    }
}
// Reset default browser styling
const ButtonStyleReset = css `
  background-image: none;
  border: none;
  font: inherit;
  font-weight: ${fontWeight.semiBold};
  text-transform: none;
  margin: 0;
  border-radius: 9999px;
  cursor: pointer;
  outline: inherit;
  transition: all 150ms cubic-bezier(0.215, 0.61, 0.355, 1);
  overflow: visible;
  appearance: none;
  line-height: 1.4;
`;
const ButtonCircularProgress = styled(CircularProgress) `
  color: ${({ $variant, $color, $disabled }) => $disabled
    ? buttonColors[$color][$variant].colorDisabled
    : buttonColors[$color][$variant].color};
`;
const StyledButton = styled.button `
  ${ButtonStyleReset}

  background: ${({ $variant, $color }) => buttonColors[$color][$variant].background};
  box-shadow: ${({ $variant, $color }) => buttonColors[$color][$variant].boxShadow};
  color: ${({ $variant, $color }) => buttonColors[$color][$variant].color};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "unset")};

  font-size: ${({ $size }) => getFontSize({ $size })};
  padding: ${({ $variant, $size, $shape }) => getPadding({ $variant, $size, $shape })};
  text-decoration: ${({ $variant }) => getTextdecoration({ $variant })};

  &:disabled {
    background: ${({ $variant, $color }) => buttonColors[$color][$variant].backgroundDisabled};
    box-shadow: ${({ $variant, $color }) => buttonColors[$color][$variant].boxShadowDisabled};
    color: ${({ $variant, $color }) => buttonColors[$color][$variant].colorDisabled};
    pointer-events: none;
  }

  &:focus-visible {
    outline-color: ${({ $variant, $color }) => buttonColors[$color][$variant].outline};
    outline-offset: 4px;
    outline-width: 1px;
    outline-style: dashed;
  }

  &:hover {
    background: ${({ $variant, $color }) => buttonColors[$color][$variant].backgroundHover};
    box-shadow: ${({ $variant, $color }) => buttonColors[$color][$variant].boxShadowHover};
    color: ${({ $variant, $color }) => buttonColors[$color][$variant].colorHover};
  }

  &:active {
    background: ${({ $variant, $color }) => buttonColors[$color][$variant].backgroundActive};
    box-shadow: ${({ $variant, $color }) => buttonColors[$color][$variant].boxShadowActive};
    color: ${({ $variant, $color }) => buttonColors[$color][$variant].colorActive};
  }
`;
const Box = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.XS};

  & svg {
    color: currentColor;
  }
`;
const buttonColors = {
    default: {
        primary: {
            color: colors.textContrast,
            colorActive: colors.textContrast,
            colorHover: colors.textContrast,
            colorDisabled: colorScale.blue[400],
            background: colors.primary,
            backgroundHover: colorScale.blue[700],
            backgroundActive: colorScale.blue[700],
            backgroundDisabled: colorScale.blue[200],
            boxShadow: `0px 0px 0px 1px ${colors.primary}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.blue[700]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.blue[700]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blue[200]}`,
            outline: colorScale.blue[700],
        },
        secondary: {
            color: colors.primary,
            colorHover: colors.primary,
            colorActive: colors.primary,
            colorDisabled: colorScale.blue[300],
            background: colors.primaryLight,
            backgroundHover: colorScale.blue[200],
            backgroundActive: colorScale.blue[200],
            backgroundDisabled: colorScale.blue[100],
            boxShadow: `0px 0px 0px 1px ${colors.primaryLight}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.blue[200]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.blue[200]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blue[100]}`,
            outline: colors.primary,
        },
        subtle: {
            color: colors.primary,
            colorHover: colors.primary,
            colorActive: colors.primary,
            colorDisabled: colorScale.blue[300],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: `0px 0px 0px 1px ${colorScale.blue[400]}`,
            boxShadowHover: `0px 0px 0px 2px ${colorScale.blue[400]}`,
            boxShadowActive: `0px 0px 0px 2px ${colorScale.blue[400]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blue[300]}`,
            outline: colors.primary,
        },
        minimal: {
            color: colors.primary,
            colorHover: colorScale.blue[700],
            colorActive: colorScale.blue[700],
            colorDisabled: colorScale.blue[300],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: "none",
            boxShadowHover: "none",
            boxShadowActive: "none",
            boxShadowDisabled: "none",
            outline: colorScale.blue[700],
        },
    },
    contrast: {
        primary: {
            color: colors.primary,
            colorHover: colors.primary,
            colorActive: colors.primary,
            colorDisabled: colorScale.blue[600],
            background: colors.neutralLight,
            backgroundHover: colorScale.blue[200],
            backgroundActive: colorScale.blue[200],
            backgroundDisabled: colorScale.blue[400],
            boxShadow: `0px 0px 0px 1px ${colors.neutralLight}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.blue[200]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.blue[200]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blue[400]}`,
            outline: colorScale.blue[200],
        },
        secondary: {
            color: colors.primary,
            colorHover: colors.primary,
            colorActive: colors.primary,
            colorDisabled: colorScale.blue[600],
            background: colorScale.blue[200],
            backgroundHover: colorScale.blue[200],
            backgroundActive: colorScale.blue[300],
            backgroundDisabled: colorScale.blue[400],
            boxShadow: `0px 0px 0px 1px ${colorScale.blue[200]}`,
            boxShadowHover: `0px 0px 0px 1px ${colors.primaryLight}`,
            boxShadowActive: `0px 0px 0px 1px ${colors.neutralLight}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blue[400]}`,
            outline: colorScale.blue[200],
        },
        subtle: {
            color: colors.textContrast,
            colorHover: colors.textContrast,
            colorActive: colors.primary,
            colorDisabled: colorScale.blue[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: `0px 0px 0px 1px ${colors.neutralLight}`,
            boxShadowHover: `0px 0px 0px 2px ${colors.neutralLight}`,
            boxShadowActive: `0px 0px 0px 2px ${colors.neutralLight}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blue[400]}`,
            outline: colors.neutralLight,
        },
        minimal: {
            color: colors.textContrast,
            colorHover: colorScale.blue[300],
            colorActive: colorScale.blue[300],
            colorDisabled: colorScale.blue[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: "none",
            boxShadowHover: "none",
            boxShadowActive: "none",
            boxShadowDisabled: "none",
            outline: colorScale.blue[300],
        },
    },
    destructive: {
        primary: {
            color: colors.textContrast,
            colorHover: colors.textContrast,
            colorActive: colors.textContrast,
            colorDisabled: colorScale.error[400],
            background: colors.systemError,
            backgroundHover: colors.systemError,
            backgroundActive: colors.systemErrorDark,
            backgroundDisabled: colorScale.error[200],
            boxShadow: `0px 0px 0px 1px ${colors.systemError}`,
            boxShadowHover: `0px 0px 0px 1px ${colors.systemErrorDark}`,
            boxShadowActive: `0px 0px 0px 1px ${colors.systemErrorDark}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.error[200]}`,
            outline: colors.systemErrorDark,
        },
        secondary: {
            color: colors.systemErrorDark,
            colorHover: colors.systemErrorDark,
            colorActive: colors.textContrast,
            colorDisabled: colorScale.error[400],
            background: colorScale.error[200],
            backgroundHover: colorScale.error[200],
            backgroundActive: colors.systemErrorDark,
            backgroundDisabled: colorScale.error[200],
            boxShadow: `0px 0px 0px 1px ${colorScale.error[200]}`,
            boxShadowHover: `0px 0px 0px 1px ${colors.systemErrorDark}`,
            boxShadowActive: `0px 0px 0px 1px ${colors.systemErrorDark}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.error[200]}`,
            outline: colors.systemErrorDark,
        },
        subtle: {
            color: colors.systemErrorDark,
            colorHover: colors.systemErrorDark,
            colorActive: colors.systemErrorDark,
            colorDisabled: colorScale.error[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: colorScale.error[200],
            backgroundDisabled: "none",
            boxShadow: `0px 0px 0px 1px ${colorScale.error[400]}`,
            boxShadowHover: `0px 0px 0px 2px ${colorScale.error[500]}`,
            boxShadowActive: `0px 0px 0px 2px ${colorScale.error[400]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.error[200]}`,
            outline: colors.systemErrorDark,
        },
        minimal: {
            color: colors.systemErrorDark,
            colorHover: colorScale.error[700],
            colorActive: colorScale.error[700],
            colorDisabled: colorScale.error[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: "none",
            boxShadowHover: "none",
            boxShadowActive: "none",
            boxShadowDisabled: "none",
            outline: colors.systemErrorDark,
        },
    },
    good: {
        primary: {
            color: colors.textContrast,
            colorHover: colors.textContrast,
            colorActive: colors.textContrast,
            colorDisabled: colorScale.good[400],
            background: colors.systemGoodDark,
            backgroundHover: colorScale.good[700],
            backgroundActive: colorScale.good[700],
            backgroundDisabled: colors.systemGoodLight,
            boxShadow: `0px 0px 0px 1px ${colors.systemGoodDark}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.good[700]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.good[700]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colors.systemGoodLight}`,
            outline: colorScale.good[700],
        },
        secondary: {
            color: colors.systemGoodDark,
            colorHover: colorScale.good[700],
            colorActive: colorScale.good[700],
            colorDisabled: colorScale.good[400],
            background: colors.systemGoodLight,
            backgroundHover: colorScale.good[300],
            backgroundActive: colorScale.good[300],
            backgroundDisabled: colors.systemGoodLight,
            boxShadow: `0px 0px 0px 1px ${colors.systemGood}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.good[600]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.good[600]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.good[400]}`,
            outline: colorScale.good[700],
        },
        subtle: {
            color: colorScale.good[700],
            colorHover: colorScale.good[700],
            colorActive: colorScale.good[700],
            colorDisabled: colorScale.good[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: `0px 0px 0px 1px ${colors.systemGood}`,
            boxShadowHover: `0px 0px 0px 2px ${colors.systemGood}`,
            boxShadowActive: `0px 0px 0px 2px ${colors.systemGood}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.good[300]}`,
            outline: colors.systemGoodDark,
        },
        minimal: {
            color: colors.systemGoodDark,
            colorHover: colorScale.good[700],
            colorActive: colorScale.good[700],
            colorDisabled: colorScale.good[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: "none",
            boxShadowHover: "none",
            boxShadowActive: "none",
            boxShadowDisabled: "none",
            outline: colorScale.good[700],
        },
    },
    warning: {
        primary: {
            color: colors.textContrast,
            colorHover: colors.textContrast,
            colorActive: colors.textContrast,
            colorDisabled: colorScale.warning[400],
            background: colors.systemWarning,
            backgroundHover: colorScale.warning[700],
            backgroundActive: colorScale.warning[700],
            backgroundDisabled: colorScale.warning[200],
            boxShadow: `0px 0px 0px 1px ${colors.systemWarning}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.warning[700]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.warning[700]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.warning[200]}`,
            outline: colorScale.warning[800],
        },
        secondary: {
            color: colors.systemWarningDark,
            colorHover: colors.systemWarningDark,
            colorActive: colors.systemWarningDark,
            colorDisabled: colorScale.warning[400],
            background: colors.systemWarningLight,
            backgroundHover: colorScale.warning[400],
            backgroundActive: colorScale.warning[400],
            backgroundDisabled: colorScale.warning[100],
            boxShadow: `0px 0px 0px 1px ${colors.systemWarning}`,
            boxShadowHover: `0px 0px 0px 1px ${colors.systemWarning}`,
            boxShadowActive: `0px 0px 0px 1px ${colors.systemWarning}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.warning[300]}`,
            outline: colors.systemWarning,
        },
        subtle: {
            color: colors.systemWarningDark,
            colorHover: colors.systemWarningDark,
            colorActive: colors.systemWarningDark,
            colorDisabled: colorScale.warning[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: `0px 0px 0px 1px ${colors.systemWarning}`,
            boxShadowHover: `0px 0px 0px 2px ${colors.systemWarning}`,
            boxShadowActive: `0px 0px 0px 2px ${colors.systemWarning}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.warning[400]}`,
            outline: colors.systemWarningDark,
        },
        minimal: {
            color: colors.systemWarningDark,
            colorHover: colorScale.warning[700],
            colorActive: colorScale.warning[700],
            colorDisabled: colorScale.warning[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: "none",
            boxShadowHover: "none",
            boxShadowActive: "none",
            boxShadowDisabled: "none",
            outline: colorScale.warning[800],
        },
    },
    neutral: {
        primary: {
            color: colors.textContrast,
            colorHover: colors.textContrast,
            colorActive: colors.textContrast,
            colorDisabled: colorScale.blueContrast[400],
            background: colors.neutralPrimary,
            backgroundHover: colors.textInactive,
            backgroundActive: colors.textInactive,
            backgroundDisabled: colorScale.blueContrast[300],
            boxShadow: `0px 0px 0px 1px ${colors.neutralPrimary}`,
            boxShadowHover: `0px 0px 0px 1px ${colors.textInactive}`,
            boxShadowActive: `0px 0px 0px 1px ${colors.textInactive}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blueContrast[300]}`,
            outline: colors.textInactive,
        },
        secondary: {
            color: colors.neutralPrimary,
            colorHover: colors.neutralPrimary,
            colorActive: colors.neutralPrimary,
            colorDisabled: colorScale.blueContrast[400],
            background: colorScale.blueContrast[200],
            backgroundHover: colorScale.blueContrast[300],
            backgroundActive: colorScale.blueContrast[300],
            backgroundDisabled: colorScale.blueContrast[200],
            boxShadow: `0px 0px 0px 1px ${colorScale.blueContrast[400]}`,
            boxShadowHover: `0px 0px 0px 1px ${colorScale.blueContrast[400]}`,
            boxShadowActive: `0px 0px 0px 1px ${colorScale.blueContrast[400]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blueContrast[400]}`,
            outline: colors.neutralPrimary,
        },
        subtle: {
            color: colors.neutralPrimary,
            colorHover: colors.neutralPrimary,
            colorActive: colors.neutralPrimary,
            colorDisabled: colorScale.blueContrast[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: `0px 0px 0px 1px ${colorScale.blueContrast[400]}`,
            boxShadowHover: `0px 0px 0px 2px ${colorScale.blueContrast[400]}`,
            boxShadowActive: `0px 0px 0px 2px ${colorScale.blueContrast[400]}`,
            boxShadowDisabled: `0px 0px 0px 1px ${colorScale.blueContrast[400]}`,
            outline: colors.neutralPrimary,
        },
        minimal: {
            color: colors.neutralPrimary,
            colorHover: colorScale.blueContrast[500],
            colorActive: colorScale.blueContrast[500],
            colorDisabled: colorScale.blueContrast[400],
            background: "none",
            backgroundHover: "none",
            backgroundActive: "none",
            backgroundDisabled: "none",
            boxShadow: "none",
            boxShadowHover: "none",
            boxShadowActive: "none",
            boxShadowDisabled: "none",
            outline: colorScale.blueContrast[500],
        },
    },
};

export { Button, ButtonCircularProgress, ButtonStyleReset };
